import { Dispatch, SetStateAction, useContext, useEffect, useState } from "react";
import { Col, Form, OverlayTrigger, Row, Tooltip } from "react-bootstrap";
import { CalendarIconSvg, InfoIconSvg, MoonIconSvg, OneWayTripIconSvg, RoundTripIconSvg, ShipIconSvg, UserIconSvg } from "../../../assets/svg";
import { createQuotation } from "../../../api/quotation.api";
import { splitDate } from "../../../utils/splitDate";
import CustomButton from "../../CustomButton/CustomButton";
import SnackbarContext from "../../../context/snackbar.context";
import dayjs from "dayjs";

interface IPriceDetailModal {
  priceData: any;
  handleGuestCount: () => { count: number, adults: number, children: number, infants: number};
  setPriceModalState: Dispatch<SetStateAction<boolean>>;
  setSuccessModalState: Dispatch<SetStateAction<boolean>>;
  payloadObj: any;
  itineraryDetails: any;
  clx?: string;
  specialPricingUpgradeOffer: { specialPricing: boolean, upgradeOffer:boolean }
}

const PriceDetailModal: React.FC<IPriceDetailModal> = ({
  priceData,
  handleGuestCount,
  setPriceModalState,
  setSuccessModalState,
  payloadObj,
  itineraryDetails,
  clx,
  specialPricingUpgradeOffer
}) => {
    const snackbarCtx = useContext(SnackbarContext);
    const [currentItineraryDetails, setCurrentItineraryDetails] = useState<any>({});

  const handleBuildQuotation = async () => {
    const response = await createQuotation({...payloadObj, special_pricing: specialPricingUpgradeOffer.specialPricing, upgrade_offer: specialPricingUpgradeOffer.upgradeOffer});
      if (response.status === 201) {
          setSuccessModalState(true);
          setPriceModalState(false);
      }
    else {
      snackbarCtx.toggleSnackbar({
        message: 'Something went wrong !',
        type: 'FAILURE',
        visible: true,
        });
    }
  };

  useEffect(() => {
    if (!!itineraryDetails?.length) {
      const tempArr = itineraryDetails.find((data: any) => data.value === payloadObj?.itineraryId);
      setCurrentItineraryDetails({ ...tempArr });
    }
  }, [payloadObj?.itineraryId]);

  const handleTripType = (tripType: string) => {
    return tripType?.includes("one") ? "One Way Trip" : "Round Trip";
  };

  return (
    <div className={`${clx}`}>
      <span className="pb-3 font-semibold text-lg text-[#3a3a3a]">Price Details</span>
      {!!priceData?.cabin_names?.length &&
        priceData?.cabin_names?.map((data: any, index: number) => {
          return (
            <Row key={index} className={index === 0 ? `mt-2` : ""}>
              <Col xs={12} sm={6}>
                <Form.Text className="input-label price-text">{priceData?.cabin_names[index]}</Form.Text>
              </Col>
              <Col xs={12} sm={6}>
                <Form.Text className="input-label price-text invoice ">
                  ₹{new Intl.NumberFormat("en-IN").format(Math.round(priceData?.cabin_fares[index]))}
                </Form.Text>
              </Col>
            </Row>
          );
        })}
      <Row>
        <Col xs={12} sm={6} className="flex flex-col">
            <Form.Text className="input-label ">Service Tax & Levies</Form.Text>
            {/* {!!priceData?.port_charges && <Form.Text className="input-label port-text-font">(Port Charges + Gratuity)</Form.Text>} */}
          </Col>
        <Col xs={12} sm={6} className={"invoice"}>
          <Form.Text className="input-label ">
            {`₹${new Intl.NumberFormat("en-IN").format(Math.round(priceData?.service_tax_and_levies))}`}
          </Form.Text>
        </Col>
      </Row>
      <Row>
        <Col xs={12} sm={6} className="flex flex-col">
            <Form.Text className="input-label ">Fuel Surcharge</Form.Text>
          </Col>
        <Col xs={12} sm={6} className={"invoice"}>
          <Form.Text className="input-label ">
            {`₹${new Intl.NumberFormat("en-IN").format(Math.round(priceData?.fuel_surcharge))}`}
          </Form.Text>
        </Col>
      </Row>
      {!!priceData?.ssr_names?.length &&
        priceData?.ssr_names?.map((data: any, index: number) => {
          return (
            <Row className="mt-2" key={index}>
              <Col xs={12} sm={6}>
                <Form.Text className="input-label price-text">{priceData?.ssr_names[index]}</Form.Text>
              </Col>
              <Col xs={12} sm={6}>
                <Form.Text className="input-label price-text invoice ">
                  ₹{new Intl.NumberFormat("en-IN").format(Math.round(priceData?.ssr_fares[index]))}
                </Form.Text>
              </Col>
            </Row>
          );
        })}
      {!!priceData?.discount && (
          <Row className="my-1">
            <Col xs={12} sm={6} className="flex flex-col">
              <span className="text-base font-normal text-[#17a300]">Discount</span>
              <Form.Text className="input-label discount-text-font">({Object?.keys(priceData?.discount_category)?.join(', ')})</Form.Text>
            </Col>
            <Col xs={12} sm={6} className={"invoice"}>
              <span className="text-base font-normal text-[#17a300]">
                {`-₹${new Intl.NumberFormat("en-IN").format(Math.round(priceData?.discount))}`}
              </span>
            </Col>
          </Row>
        )}
      <Row>
        <Col xs={12} sm={6}>
          <Form.Text className="input-label ">GST</Form.Text>
        </Col>
        <Col xs={12} sm={6} className={"invoice"}>
          <Form.Text className="input-label ">
            {`₹${new Intl.NumberFormat("en-IN").format(Math.round(priceData?.gst))}`}
          </Form.Text>
        </Col>
      </Row>
      <Row className="mt-2 pl-3 font-semibold text-lg text-[#3a3a3a]">Itinerary Info</Row>
      <div className="mt-2 border-1 border-b-[#D0566E] px-3 py-2 flex justify-between">
        <div>
          <div
            className="d-flex align-items-center gap-1 mt-2"
            style={{ fontSize: "13px", fontWeight: "500", lineHeight: "16.93px" }}
          >
            {currentItineraryDetails?.tripType === "one_way" ? (
              <OneWayTripIconSvg height={18} width={16} />
            ) : (
              <RoundTripIconSvg height={18} width={18} />
            )}
            <p className="my-0 mx-1">
              {handleTripType(currentItineraryDetails?.tripType)} from :{" "}
              <span style={{ fontWeight: 400 }}>
                {currentItineraryDetails?.originPort} to {currentItineraryDetails?.destinationPort}
              </span>
            </p>
          </div>
          <div
            className="d-flex align-items-center gap-1 mt-2"
            style={{ fontSize: "13px", fontWeight: "500", lineHeight: "16.93px" }}
          >
            <MoonIconSvg fill="#7a7a7a" height={16} width={17} />
            <p className="my-0 mx-1">{currentItineraryDetails?.nightCount} Nights</p>
          </div>
          <div
            className="d-flex align-items-center gap-1 -ml-0.5  mt-2"
            style={{ fontSize: "13px", fontWeight: "500", lineHeight: "16.93px" }}
          >
            <ShipIconSvg fill="#7a7a7a" height={20} width={20} />
            <p className="my-0 ml-0.5 mr-1 text-capitalize">{currentItineraryDetails?.shipName?.toLowerCase()}</p>
          </div>
          <div
            className="d-flex align-items-center gap-1 mt-2"
            style={{ fontSize: "13px", fontWeight: "500", lineHeight: "16.93px" }}
          >
            <CalendarIconSvg fill="#7a7a7a" height={18} width={18} />
            <p className="my-0 mx-1">
              {dayjs(splitDate(currentItineraryDetails?.startTime)).format("DD MMM YYYY")} -{" "}
              {dayjs(splitDate(currentItineraryDetails?.endTime)).format("DD MMM YYYY")}
            </p>
          </div>
          <div
            className="d-flex align-items-center gap-1 mt-2"
            style={{ fontSize: "13px", fontWeight: "500", lineHeight: "16.93px" }}
          >
            <UserIconSvg fill="#7a7a7a" height={18} width={18} />
            <p className="my-0 mx-1">
              {handleGuestCount().count} {handleGuestCount().count > 1 ? 'Guests' : 'Guest'}
            </p>
            <OverlayTrigger delay={{ hide: 0, show: 50 }} placement="bottom"
            overlay={
              <Tooltip>
                  {`Adult: ${handleGuestCount()?.adults}`}
                  <br />
                  {`Children: ${handleGuestCount()?.children}`}
                  <br />
                  {`Infant: ${handleGuestCount()?.infants}`}
              </Tooltip>
            }>
            <span className="cursor-pointer">
              <InfoIconSvg fill="#D0566E" />
            </span>
          </OverlayTrigger>
          </div>
        </div>
        <div className="flex flex-col items-end">
          {!!priceData?.total && !!priceData?.discount && (
                <div style={{ position: "relative", overflow: "hidden" }}>
                  <div
                    className="w-[calc(100%-1rem)]  h-[2px] bg-[#D8666F]"
                    style={{ position: "absolute", top: 10, transform: "rotate(-10deg)" }}
                  ></div>
                  <span
                    style={{
                      fontSize: "21px",
                      fontWeight: "500",
                      lineHeight: "27.34px",
                      textAlign: "left",
                      color: "#7A7A7A",
                    }}
                  >
                  {`₹${new Intl.NumberFormat("en-IN").format(Math.round(priceData?.total))}`}
                  </span>
                </div>
              )}
          {!!priceData?.total && (
            <div>
              <p
                className="mt-2"
                style={{
                  fontSize: "24px",
                  fontWeight: "700",
                  lineHeight: "31.25px",
                  textAlign: "left",
                  color: "#D8666F",
                }}
              >
                {`₹${new Intl.NumberFormat("en-IN").format(Math.round(priceData?.total - priceData?.discount))}`}
              </p>
            </div>
          )}
          {!!priceData?.total && (
            <div>
              <p
                style={{
                  fontSize: "20px",
                  fontWeight: "400",
                  lineHeight: "15.62px",
                  textAlign: "left",
                  color: "#D8666F",
                }}
              >
                {!!priceData?.discount ? 'Discount Price' : 'Total Price'}
              </p>
            </div>
          )}
        </div>
      </div>
      <div className="flex gap-2 mt-4 w-100 justify-end">
        <CustomButton fillColor={false} onButtonClick={() => setPriceModalState(false)}>Edit</CustomButton>
        <CustomButton onButtonClick={handleBuildQuotation}>Send</CustomButton>
      </div>
    </div>
  );
};
export default PriceDetailModal;
